import { Amplify } from "aws-amplify";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import "./App.css";
import awsconfig from "./aws-exports";
import { ApiProvider } from "./contexts/ApiContext";
import { AppProvider } from "./contexts/AppContext";
import { AuthProvider } from "./contexts/AuthContext";
import { ErrorProvider } from "./contexts/ErrorContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import MainLayout from "./layouts/MainLayout";
import GuestLayout from "./layouts/GuestLayout";
import {
  CustomizeReport,
  EggFreezeCalculatorPage,
  HoromoneProtocolsPage,
  LoginPage,
  OocyteQualityAssessmentPage,
  SignUpPage,
  EmbryoQualityAssessment,
  ImplantationWindowPage,
  DashboardPage,
  SettingsPage,
  ManagePage,
  SpermAnalysisPage,
} from "./pages";
import PasswordRecovery from "./pages/Auth/PasswordRecovery";
import { LoginProvider } from "./contexts/LoginContext";
import NewPassword from "./pages/Auth/NewPassword";

const apiBaseUrl = process.env.REACT_APP_API_GATEWAY_URL || "";
const apiPublicToken = process.env.REACT_APP_API_GATEWAY_KEY || "";

console.info("apiBaseUrl", apiBaseUrl);

Amplify.configure(awsconfig);

if (!apiBaseUrl?.length) {
  throw "REACT_APP_API_GATEWAY_URL is not defined in .env";
}

if (!apiPublicToken?.length) {
  throw "REACT_APP_API_GATEWAY_KEY is not defined in .env";
}

const NavigateAndPreserveQuery = ({ to }: { to: string }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} />;
};

function App() {
  return (
    <ErrorProvider>
      <AppProvider>
        <ApiProvider publicToken={apiPublicToken} baseUrl={apiBaseUrl}>
          <AuthProvider>
            <WebSocketProvider>
              <BrowserRouter>
                <Routes>
                  <Route
                    element={<NavigateAndPreserveQuery to="/login/forgot" />}
                    path="/forgot"
                  />
                  <Route
                    element={<NavigateAndPreserveQuery to="/signup/verify" />}
                    path="/verify"
                  />
                  <Route path="/" element={<Navigate to="/dashboard" />} />
                  <Route
                    element={
                      <MainLayout>
                        <DashboardPage />
                      </MainLayout>
                    }
                    path="/dashboard"
                  />
                  <Route
                    element={
                      <GuestLayout>
                        <LoginPage />
                      </GuestLayout>
                    }
                    path="/login/*"
                  />
                  <Route
                    element={
                      <GuestLayout>
                        <SignUpPage />
                      </GuestLayout>
                    }
                    path="/signup/*"
                  />
                  <Route
                    element={
                      <MainLayout>
                        <EggFreezeCalculatorPage />
                      </MainLayout>
                    }
                    path="/calc/*"
                  />
                  <Route
                    element={
                      <MainLayout>
                        <OocyteQualityAssessmentPage />
                      </MainLayout>
                    }
                    path="/oocyte/*"
                  />
                  <Route
                    element={
                      <MainLayout>
                        <EmbryoQualityAssessment />
                      </MainLayout>
                    }
                    path="/embryo/*"
                  />
                  <Route
                    element={
                      <MainLayout>
                        <HoromoneProtocolsPage />
                      </MainLayout>
                    }
                    path="/hormone/*"
                  />
                  <Route
                    element={
                      <MainLayout>
                        <ImplantationWindowPage />
                      </MainLayout>
                    }
                    path="/implantation/*"
                  />
                  <Route
                    element={
                      <MainLayout>
                        <SpermAnalysisPage />
                      </MainLayout>
                    }
                    path="/sperm/*"
                  />
                  <Route
                    element={
                      <MainLayout>
                        <ManagePage />
                      </MainLayout>
                    }
                    path="/manage"
                  />
                  <Route
                    element={
                      <MainLayout>
                        <SettingsPage />
                      </MainLayout>
                    }
                    path="/settings"
                  />
                  <Route
                    element={
                      <MainLayout>
                        <CustomizeReport />
                      </MainLayout>
                    }
                    path="/report"
                  />
                 
                </Routes>
              </BrowserRouter>
            </WebSocketProvider>
          </AuthProvider>
        </ApiProvider>
      </AppProvider>
    </ErrorProvider>
  );
}

export default App;
